import * as React from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, Alert } from 'reactstrap';
import { IconButton } from '@material-ui/core';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EventIcon from '@mui/icons-material/Event';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import styled from 'styled-components';
import AppButton from '../../../components/AppButton';
import { AutocompleteTextField, ComboField } from '../../../components/Forms/fields';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import {
    addPaymentToClientAppointmentsAsync,
    createClientPackAsync,
    getClientAsync,
    getClientAppointmentsAsync,
    getClientPacksAsync,
    getClientsAsync,
    getPackTemplatesAsync,
    updateClientPackAsync,
} from '../../../ajax/requests';
import { ClientData, ClientPack } from '../../../models';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { getSettings } from '../../../store/settings/selectors';
import { loadSettings } from '../../../store/settings/actions';
import { getMyUser, isAdministrator } from '../../../store/session/selectors';
import CashRegisterProcessModal from '../../../components/CashRegister/CashRegisterProcessModal';
import { Selectors as GeneralSelectors, Actions as GeneralActions } from '../../../store/general';
import { useDispatch, useSelector } from 'react-redux';
import { alertError, alertSuccess } from '../../../store/alerts/actions';
import { getErrorMessage } from '../../../ajax/errors';
import { entitiesLoaded } from '../../../store/entities/actions';
import { InvoicesTable } from '../../Clients/ClientInvoicesTable';
import type { AppointmentEntity, ClientEntity, ClientPackEntity } from '../../../store/entities/state';
import {
    buildAppointmentSources,
    buildClientPackFromTemplate,
    buildManualPackPayment,
    buildPackSources,
    buildSubscriptionSources,
    getSourceOptionLabel,
    QUICK_SOURCE_GROUP_AVAILABLE,
    QUICK_SOURCE_GROUP_PENDING,
    QuickSource,
    SourceKind,
    sourceKey,
} from './cashRegisterQuickActionHelpers';

const QuickFlow = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
`;

const ModeButtons = styled.div`
    display: flex;
    gap: var(--spacer-S);
    flex-wrap: wrap;
    justify-content: center;
`;

const ModeButton = styled(AppButton)`
    width: 14rem;
`;

const FooterButton = styled(AppButton)`
    white-space: nowrap;
`;

const HeaderBar = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    padding: var(--spacer-S) var(--spacer-M);
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
`;

const HeaderSide = styled.div`
    min-width: 7rem;
    display: flex;
    align-items: center;
`;

const HeaderSideRight = styled(HeaderSide)`
    justify-content: flex-end;
`;

const HeaderTitle = styled.div`
    flex: 1;
    text-align: center;
    font-weight: 600;
`;

const isDefinedString = (value: string | undefined): value is string => !!value;

interface CashRegisterQuickActionProps {
    controlledOpen?: boolean;
    onControlledClose?: () => void;
    initialClientId?: string;
}

const CashRegisterQuickAction: React.FC<CashRegisterQuickActionProps> = ({ controlledOpen, onControlledClose, initialClientId }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();
    const settings = useSelector(getSettings);
    const selectedCashRegisterName = useSelector(GeneralSelectors.getSelectedCashRegisterName);
    const isAdmin = useSelector(isAdministrator);
    const currentUser = useSelector(getMyUser);

    const [view, setView] = React.useState<'sale' | 'manage'>('sale');
    const [switchRegisterOpen, setSwitchRegisterOpen] = React.useState(false);
    const [pendingRegisterName, setPendingRegisterName] = React.useState('');

    const canApprove = React.useMemo(() => {
        if (isAdmin) return true;
        const approvers = settings.cashRegisterSettings?.nonAdminApprovers ?? [];
        return currentUser?.username ? approvers.includes(currentUser.username) : false;
    }, [isAdmin, settings.cashRegisterSettings?.nonAdminApprovers, currentUser?.username]);

    const manageRegister = selectedCashRegisterName
        ? { cashRegisterName: selectedCashRegisterName }
        : { username: currentUser?.username };
    const manageDisplayName = selectedCashRegisterName || t(TK.cashRegisterOwnRegister);

    const isControlled = controlledOpen !== undefined;
    const [isOpen, setIsOpen] = React.useState(false);
    const modalIsOpen = isControlled ? (controlledOpen ?? false) : isOpen;
    const [clients, setClients] = React.useState<ClientData[]>([]);
    const [clientId, setClientId] = React.useState('');
    const [mode, setMode] = React.useState<SourceKind | null>(null);
    const [sources, setSources] = React.useState<QuickSource[]>([]);
    const [sourceValue, setSourceValue] = React.useState('');
    const [paymentMethod, setPaymentMethod] = React.useState('');
    const [isLoadingSources, setIsLoadingSources] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    // When a new pack is created from a template before showing invoice mode, keep it here
    const [createdPack, setCreatedPack] = React.useState<ClientPack | null>(null);
    const [invoiceMode, setInvoiceMode] = React.useState(false);

    const loadSourcesRequestRef = React.useRef(0);
    const searchClientsRequestRef = React.useRef(0);

    const sharedRegisterNames = React.useMemo(
        () => settings.cashRegisterSettings?.enabled ? (settings.cashRegisterSettings.sharedRegisterNames ?? []) : [],
        [settings.cashRegisterSettings],
    );
    const clientOptions = React.useMemo(() => clients.map(client => client.id).filter(isDefinedString), [clients]);
    const clientsById = React.useMemo(() => Object.fromEntries(clients.filter(client => !!client.id).map(client => [client.id, client])), [clients]);
    const selectedClient = clientsById[clientId];
    const sourceOptions = React.useMemo(() => sources.map(sourceKey), [sources]);
    const sourcesByKey = React.useMemo(() => Object.fromEntries(sources.map(source => [sourceKey(source), source])), [sources]);
    const selectedSource = sourcesByKey[sourceValue];

    const reset = React.useCallback(() => {
        setClientId('');
        setSources([]);
        setSourceValue('');
        setPaymentMethod('');
        setMode(null);
        setCreatedPack(null);
        setInvoiceMode(false);
        setView('sale');
    }, []);

    const open = React.useCallback(() => {
        if (!isControlled) setIsOpen(true);
        dispatch(loadSettings());
        getClientsAsync(undefined, 20, 0).then(setClients).catch(() => undefined);
    }, [isControlled, dispatch]);

    const close = React.useCallback(() => {
        if (isControlled) {
            onControlledClose?.();
        } else {
            setIsOpen(false);
        }
        reset();
    }, [isControlled, onControlledClose, reset]);

    React.useEffect(() => {
        if (!isControlled || !controlledOpen) return;
        if (initialClientId) {
            Promise.all([
                getClientsAsync(undefined, 20, 0),
                getClientAsync(initialClientId).catch(() => null),
            ]).then(([list, specific]) => {
                const merged = specific && !list.some(c => c.id === specific.id)
                    ? [specific, ...list]
                    : list;
                setClients(merged);
                setClientId(initialClientId);
            }).catch(() => undefined);
        } else {
            getClientsAsync(undefined, 20, 0).then(setClients).catch(() => undefined);
        }
    }, [isControlled, controlledOpen, initialClientId]);

    const searchClients = React.useCallback((keyword: string) => {
        if (keyword.trim().length < 2) return;
        const requestId = ++searchClientsRequestRef.current;
        getClientsAsync(keyword, 20, 0)
            .then(result => { if (requestId === searchClientsRequestRef.current) setClients(result); })
            .catch(() => undefined);
    }, []);

    const loadSourcesForMode = React.useCallback((nextClientId: string, nextMode: SourceKind) => {
        setSourceValue('');
        setInvoiceMode(false);
        setCreatedPack(null);
        if (!nextClientId) {
            setSources([]);
            return;
        }

        const requestId = ++loadSourcesRequestRef.current;
        setIsLoadingSources(true);
        Promise.all([
            nextMode === 'appointment'
                ? getClientAppointmentsAsync(nextClientId, undefined, undefined, 100, 0)
                : Promise.resolve([]),
            nextMode !== 'appointment'
                ? Promise.all([getClientPacksAsync(nextClientId, 100, 0), getPackTemplatesAsync()])
                : Promise.resolve([[], []] as [never[], never[]]),
        ])
            .then(([appointments, packsAndTemplates]) => {
                if (requestId !== loadSourcesRequestRef.current) return;
                const [clientPacks, templates] = packsAndTemplates as [typeof appointments, ReturnType<typeof getPackTemplatesAsync> extends Promise<infer T> ? T : never];
                if (nextMode === 'appointment') {
                    setSources(buildAppointmentSources(appointments as any, t, generalSettings));
                } else if (nextMode === 'pack') {
                    setSources(buildPackSources(clientPacks as any, templates as any, t));
                } else {
                    setSources(buildSubscriptionSources(clientPacks as any, templates as any, t));
                }
            })
            .catch(error => {
                if (requestId !== loadSourcesRequestRef.current) return;
                dispatch(alertError(getErrorMessage(error) ?? t(TK.somethingWentWrong)));
            })
            .finally(() => {
                if (requestId !== loadSourcesRequestRef.current) return;
                setIsLoadingSources(false);
            });
    }, [dispatch, generalSettings, t]);

    const selectMode = React.useCallback((nextMode: SourceKind) => {
        setMode(nextMode);
        setSources([]);
        setSourceValue('');
        setCreatedPack(null);
        setInvoiceMode(false);
        if (clientId) {
            loadSourcesForMode(clientId, nextMode);
        }
    }, [clientId, loadSourcesForMode]);

    const selectClient = React.useCallback((nextClientId: string) => {
        setClientId(nextClientId);
        setSources([]);
        setSourceValue('');
        setCreatedPack(null);
        setInvoiceMode(false);
        if (nextClientId && mode) {
            loadSourcesForMode(nextClientId, mode);
        }
    }, [loadSourcesForMode, mode]);

    // Creates a new client pack from template if source is a template, returns the pack to use for payment
    const resolvePackForSource = React.useCallback(async (source: QuickSource): Promise<ClientPack | null> => {
        if (source.pack) return source.pack;
        if (!source.packTemplate || !selectedClient?.id) return null;

        const newPack = buildClientPackFromTemplate(source.packTemplate, selectedClient.id, selectedClient.name ?? '');
        const newId = await createClientPackAsync(newPack);
        return { ...newPack, id: newId };
    }, [selectedClient]);

    const addPayment = React.useCallback(async () => {
        if (!selectedClient?.id || !selectedSource || !paymentMethod) return;

        setIsSaving(true);
        try {
            if (selectedSource.appointment) {
                const updatedAppointments = await addPaymentToClientAppointmentsAsync(
                    selectedClient.id,
                    [selectedSource.appointment.id],
                    paymentMethod,
                    selectedCashRegisterName || undefined);
                dispatch(entitiesLoaded('appointments', updatedAppointments, [], 'AppendResult'));
            } else {
                const pack = await resolvePackForSource(selectedSource);
                if (!pack) return;
                const payment = buildManualPackPayment(paymentMethod, selectedSource.amountDue, selectedCashRegisterName || undefined);
                const updatedPack = { ...pack, payments: [...(pack.payments ?? []), payment] };
                await updateClientPackAsync(updatedPack);
                dispatch(entitiesLoaded('clientPacks', [updatedPack], [], 'AppendResult'));
            }
            dispatch(alertSuccess(t(TK.savedSuccessfully)));
            close();
        } catch (error) {
            const message = getErrorMessage(error);
            dispatch(alertError(message ?? t(TK.somethingWentWrong)));
        } finally {
            setIsSaving(false);
        }
    }, [close, dispatch, paymentMethod, resolvePackForSource, selectedCashRegisterName, selectedClient?.id, selectedSource, t]);

    const openInvoiceMode = React.useCallback(async () => {
        if (!selectedSource || !paymentMethod) return;
        if (selectedSource.appointment || selectedSource.pack) {
            setInvoiceMode(true);
            return;
        }
        // Template source: create the pack first, then show invoice mode
        if (!selectedSource.packTemplate || !selectedClient?.id) return;
        setIsSaving(true);
        try {
            const pack = await resolvePackForSource(selectedSource);
            if (!pack) return;
            dispatch(entitiesLoaded('clientPacks', [pack], [], 'AppendResult'));
            setCreatedPack(pack);
            setInvoiceMode(true);
        } catch (error) {
            dispatch(alertError(getErrorMessage(error) ?? t(TK.somethingWentWrong)));
        } finally {
            setIsSaving(false);
        }
    }, [dispatch, paymentMethod, resolvePackForSource, selectedClient?.id, selectedSource, t]);

    const invoicePack = createdPack ?? selectedSource?.pack ?? null;

    const noSourcesText = mode === 'appointment'
        ? (isLoadingSources ? t(TK.loading) : t(TK.cashRegisterNoUnpaidAppointments))
        : mode === 'pack'
            ? (isLoadingSources ? t(TK.loading) : t(TK.cashRegisterNoPacks))
            : (isLoadingSources ? t(TK.loading) : t(TK.cashRegisterNoSubscriptions));

    const groupLabel = (group: string) =>
        group === QUICK_SOURCE_GROUP_PENDING ? t(TK.cashRegisterGroupPending) : t(TK.cashRegisterGroupAvailable);

    return (
        <>
            {!isControlled && (
                <AppButton
                    id="calendar-cash-register-button"
                    color="secondary"
                    outline
                    onClick={open}
                    startIcon={<PointOfSaleIcon />}
                >
                    {t(TK.cashRegister)}
                </AppButton>
            )}

            <Modal isOpen={modalIsOpen} toggle={close} size="lg">
                <HeaderBar>
                    <HeaderSide>
                        {view === 'sale' ? (
                            <AppButton color="secondary" outline size="sm" startIcon={<SwapHorizIcon fontSize="small" />} onClick={() => setView('manage')}>
                                {t(TK.cashRegisterManage)}
                            </AppButton>
                        ) : (
                            <AppButton
                                color="secondary"
                                outline
                                size="sm"
                                startIcon={<ArrowBackIcon fontSize="small" />}
                                onClick={() => setView('sale')}
                            >
                                {t(TK.cashRegisterBackToSale)}
                            </AppButton>
                        )}
                    </HeaderSide>
                    <HeaderTitle>
                        {view === 'sale' ? t(TK.cashRegisterQuickSale) : manageDisplayName}
                    </HeaderTitle>
                    <HeaderSideRight>
                        {view === 'manage' && sharedRegisterNames.length > 0 && (
                            <AppButton
                                color="secondary"
                                outline
                                size="sm"
                                startIcon={<SwapHorizIcon fontSize="small" />}
                                onClick={() => { setPendingRegisterName(selectedCashRegisterName ?? ''); setSwitchRegisterOpen(true); }}
                            >
                                {selectedCashRegisterName || t(TK.cashRegisterOwnRegister)}
                            </AppButton>
                        )}
                        <IconButton size="small" onClick={close} title={t(TK.cancel)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </HeaderSideRight>
                </HeaderBar>

                {view === 'manage' && (
                    <CashRegisterProcessModal
                        embedded
                        register={manageRegister}
                        displayName={manageDisplayName}
                        canApprove={canApprove}
                        sharedRegisterNames={sharedRegisterNames}
                        onClose={() => setView('sale')}
                    />
                )}

                {view === 'sale' && (
                <>
                <ModalBody>
                    <QuickFlow>
                        <AutocompleteTextField
                            id="cash-register-client"
                            label={t(TK.client)}
                            options={clientOptions}
                            value={clientId}
                            optionLabelResolver={option => clientsById[option]?.name ?? option}
                            onTextChanged={searchClients}
                            onValueChanged={selectClient}
                        />
                        {sharedRegisterNames.length > 0 && (
                            <ComboField
                                label={t(TK.cashRegisterCurrentRegister)}
                                value={selectedCashRegisterName ?? ''}
                                options={[
                                    { value: '', label: t(TK.cashRegisterOwnRegister) },
                                    ...sharedRegisterNames.map(name => ({ value: name, label: name })),
                                ]}
                                onChange={value => dispatch(GeneralActions.selectCashRegisterName(value ?? ''))}
                            />
                        )}
                        {clientId && (
                            <ModeButtons>
                                <ModeButton
                                    size="lg"
                                    color={mode === 'appointment' ? 'primary' : 'secondary'}
                                    outline={mode !== 'appointment'}
                                    onClick={() => selectMode('appointment')}
                                    startIcon={<EventIcon />}
                                >
                                    {t(TK.cashRegisterAddAppointment)}
                                </ModeButton>
                                <ModeButton
                                    size="lg"
                                    color={mode === 'pack' ? 'primary' : 'secondary'}
                                    outline={mode !== 'pack'}
                                    onClick={() => selectMode('pack')}
                                    startIcon={<Inventory2Icon />}
                                >
                                    {t(TK.cashRegisterAddPack)}
                                </ModeButton>
                                <ModeButton
                                    size="lg"
                                    color={mode === 'subscription' ? 'primary' : 'secondary'}
                                    outline={mode !== 'subscription'}
                                    onClick={() => selectMode('subscription')}
                                    startIcon={<AutorenewIcon />}
                                >
                                    {t(TK.cashRegisterAddSubscription)}
                                </ModeButton>
                            </ModeButtons>
                        )}
                        {mode && (
                            <AutocompleteTextField
                                id="cash-register-source"
                                label={mode === 'appointment' ? t(TK.appointment) : mode === 'pack' ? t(TK.pack) : t(TK.subscription)}
                                options={sourceOptions}
                                value={sourceValue}
                                disabled={!clientId || isLoadingSources}
                                optionLabelResolver={option => sourcesByKey[option] ? getSourceOptionLabel(sourcesByKey[option]) : option}
                                groupBy={mode !== 'appointment' ? option => sourcesByKey[option]?.group ?? QUICK_SOURCE_GROUP_AVAILABLE : undefined}
                                groupLabelResolver={mode !== 'appointment' ? groupLabel : undefined}
                                onValueChanged={value => {
                                    setSourceValue(value);
                                    setCreatedPack(null);
                                    setInvoiceMode(false);
                                }}
                                noOptionsText={noSourcesText}
                            />
                        )}
                        {mode && (
                            <ComboField
                                label={t(TK.selectPaymentMethod)}
                                value={paymentMethod}
                                options={generalSettings.paymentMethods.map(method => ({ value: method, label: method }))}
                                onChange={value => setPaymentMethod(value ?? '')}
                            />
                        )}
                        <Alert color="secondary" style={{ margin: 0 }}>
                            {t(TK.cashRegisterQuickFlowInfo)}
                        </Alert>
                        {invoiceMode && selectedClient && (selectedSource?.appointment || invoicePack) && (
                            <InvoicesTable
                                clientId={selectedClient.id!}
                                client={selectedClient as ClientEntity}
                                invoices={[]}
                                appointment={selectedSource?.appointment as AppointmentEntity}
                                clientPack={invoicePack as ClientPackEntity}
                                createNew
                                hideCreateButton
                                createPaymentMethod={paymentMethod}
                                onCreated={close}
                                onUpdate={() => undefined}
                                onModalClose={() => { setInvoiceMode(false); setCreatedPack(null); }}
                            />
                        )}
                    </QuickFlow>
                </ModalBody>
                <ModalFooter>
                    <FooterButton color="primary" disabled={!selectedSource || !paymentMethod || isSaving} onClick={addPayment} startIcon={<PaymentsIcon />}>
                        {t(TK.addManualPayment)}
                    </FooterButton>
                    <FooterButton color="success" disabled={!selectedSource || !paymentMethod || isSaving} onClick={openInvoiceMode} startIcon={<ReceiptLongIcon />}>
                        {t(TK.invoiceReceipt)}
                    </FooterButton>
                    <AppButton color="secondary" onClick={close}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
                </>
                )}
            </Modal>

            <Modal isOpen={switchRegisterOpen} toggle={() => setSwitchRegisterOpen(false)}>
                <ModalHeader toggle={() => setSwitchRegisterOpen(false)}>
                    {t(TK.cashRegisterChangeRegister)}
                </ModalHeader>
                <ModalBody>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 'var(--spacer-M)' }}>
                        {t(TK.cashRegisterSelectRegister)}
                    </p>
                    <ComboField
                        label={t(TK.cashRegisterCurrentRegister)}
                        value={pendingRegisterName}
                        options={[
                            { value: '', label: t(TK.cashRegisterOwnRegister) },
                            ...sharedRegisterNames.map(name => ({ value: name, label: name })),
                        ]}
                        onChange={value => setPendingRegisterName(value ?? '')}
                    />
                </ModalBody>
                <ModalFooter>
                    <AppButton color="secondary" onClick={() => setSwitchRegisterOpen(false)}>
                        {t(TK.cancel)}
                    </AppButton>
                    <AppButton
                        color="primary"
                        onClick={() => {
                            dispatch(GeneralActions.selectCashRegisterName(pendingRegisterName));
                            setSwitchRegisterOpen(false);
                        }}
                    >
                        {t(TK.confirm)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default CashRegisterQuickAction;
